import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, VideoGrid } from "@/components/VideoCard";
import { api } from "@/lib/api";

const KINDS = [["", "Everything"], ["movie", "Movies"], ["episode", "Episodes"], ["video", "Other videos"]];
const STATUS = [["all", "Any status"], ["unwatched", "Unwatched"], ["in_progress", "In progress"], ["watched", "Watched"]];
const SORTS = [["recent", "Recently added"], ["title", "Title A–Z"], ["year", "Release year"], ["duration", "Longest first"]];

const Chips = ({ items, value, onChange, testid }) => (
  <div className="flex gap-1 rounded-full border border-zinc-200 bg-white p-1" data-testid={testid}>
    {items.map(([v, l]) => (
      <button key={v || "all"} onClick={() => onChange(v)} data-testid={`${testid}-${v || "all"}`}
        className={`h-8 rounded-full px-3 text-xs transition-colors ${value === v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>{l}</button>
    ))}
  </div>
);

const Pick = ({ value, onChange, items, testid, width = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-10 ${width} bg-white`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-option-${v}`}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Library({ kind: fixedKind, title, eyebrow }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [f, setF] = useState({ source: "", library: "all", kind: fixedKind, genre: "all", resolution: "all", status: "all", sort: "recent" });
  const [facets, setFacets] = useState({ genres: [], resolutions: [], libraries: [] });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/videos/facets", { params: { kind: fixedKind || "" } }).then((r) => setFacets(r.data)); }, [fixedKind]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  const params = (skip) => ({ q: debounced, source: f.source, kind: f.kind, sort: f.sort, skip, limit: 60, library: f.library === "all" ? "" : f.library,
    genre: f.genre === "all" ? "" : f.genre, resolution: f.resolution === "all" ? "" : f.resolution, status: f.status === "all" ? "" : f.status });
  useEffect(() => {
    setBusy(true);
    api.get("/videos", { params: params(0) }).then((r) => setData(r.data)).finally(() => setBusy(false));
  }, [debounced, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const more = async () => {
    setBusy(true);
    const r = await api.get("/videos", { params: params(data.items.length) });
    setData({ total: r.data.total, items: [...data.items, ...r.data.items] });
    setBusy(false);
  };
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v, ...(k === "source" ? { library: "all" } : {}) }));
  const libs = (facets.libraries || []).filter((l) => !f.source || l.source === f.source);
  const libItems = [["all", "All libraries"], ...libs.map((l) => [`${l.source}:${l.name}`, `${l.name} (${l.count})`])];
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} testid="library-title" sub={data ? `${data.total} ${data.total === 1 ? "video" : "videos"}` : " "} />
      <div className="mb-10 flex flex-wrap items-center gap-3" data-testid="library-filters">
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, shows, files…" className="h-10 bg-white pl-9" data-testid="library-search-input" />
        </div>
        {libs.length > 0 && <Pick value={f.library} onChange={set("library")} items={libItems} testid="library-library-select" width="w-60" />}
        {!fixedKind && <Chips items={KINDS} value={f.kind} onChange={set("kind")} testid="library-kind" />}
        <Pick value={f.genre} onChange={set("genre")} items={[["all", "All genres"], ...facets.genres.map((g) => [g, g])]} testid="library-genre-select" />
        <Pick value={f.resolution} onChange={set("resolution")} items={[["all", "Any quality"], ...facets.resolutions.map((r) => [r, r])]} testid="library-resolution-select" width="w-36" />
        <Pick value={f.status} onChange={set("status")} items={STATUS} testid="library-status-select" />
        <Pick value={f.sort} onChange={set("sort")} items={SORTS} testid="library-sort-select" width="w-44" />
        {busy && <Loader2 size={16} className="animate-spin text-zinc-400" />}
      </div>
      {data && (data.items.length ? <VideoGrid videos={data.items} wide={f.kind === "video"} /> : (
        <EmptyState title="Nothing here yet" text="Try a different search or filter — or add more folders and libraries on the Sources page." testid="library-empty" />
      ))}
      {data && data.items.length < data.total && (
        <button onClick={more} disabled={busy} data-testid="library-load-more"
          className="mx-auto mt-12 flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 text-sm hover:border-zinc-900">Load more</button>
      )}
    </div>
  );
}
